<template>
  <div
    class="playlist-layer graphic"
    @click.prevent="onClickTimelineItem"
    v-click-outside="saveName"
  >
    <div class="layer-info" @dblclick.prevent="!isEditingName ? toggleCollapse() : null">
      <button
        class="drag-handle tb-btn tb-btn-link sm low-em"
        title="Rearrange"
      >
        <span class="icon icon-drag-handle"></span>
      </button>

      <div class="layer-title margin-left-xs">
        <button
          class="tb-btn tb-btn-link sm low-em collapse-btn"
          :class="isCollapsed ? 'collapse-btn--rotated' : ''"
          :title="isCollapsed ? 'Expand' : 'Collapse'"
          @click.stop="toggleCollapse"
        >
          <span class="fas fa-chevron-down"></span>
        </button>

        <div class="layer-name--editable">
          <div
            v-if="!isEditingName"
            :title="title"
            class="title-text"
            @dblclick.stop="editName"
          >
            {{ title }}
          </div>
          <span v-if="!isEditingName" @click.stop="editName" class="icon icon-pencil"></span>

          <input
            v-if="isEditingName"
            type="text"
            v-model="name"
            autofocus
            ref="nameInput"
            @keyup.enter="saveName"
            @keyup.esc="cancelEditingName"
          />
          <span v-if="isEditingName" @click.stop="saveName" class="fas fa-check" />
          <span v-if="isEditingName" @click.stop="cancelEditingName" class="icon icon-cross" />
        </div>
      </div>

      <!-- Show number of elements in group if collapsed and not editing name -->
      <div class="layer-description capitalize margin-left-xxs">
        <div v-if="!isEditingName" class="description-text">
          {{ graphicsCount }}
        </div>

        <duration
          v-if="showDuration"
          :duration.sync="item.displayTime"
        />
      </div>

      <preview-live-switch :item='item'></preview-live-switch>

      <dropdown position="left" class="more-actions margin-left-xxs">
        <template slot="trigger">
          <button class="tb-btn tb-btn-text sm medium-em" title="More actions">
            <span class="icon icon-more-vertical"></span>
          </button>
        </template>
        <template slot="dropdown-body">
          <button
            class="dropdown-list-item"
            v-on:click="duplicateItem"
          >
            <span class="dropdown-list-item-label">Duplicate in Playlist</span>
          </button>

          <button
            v-if="canMergeUp"
            class="dropdown-list-item"
            v-on:click="mergeUp"
            title="Merge with above graphic or group in the playlist"
          >
            <span class="dropdown-list-item-label">Merge Up</span>
          </button>

          <button
            v-if="canMergeDown"
            class="dropdown-list-item"
            v-on:click="mergeDown"
            title="Merge with below graphic or group in the playlist"
          >
            <span class="dropdown-list-item-label">Merge Down</span>
          </button>

          <hr>

          <button
            class="dropdown-list-item"
            v-on:click="removeItem"
          >
            <span class="dropdown-list-item-label warning">
              Remove
            </span>
          </button>
        </template>
      </dropdown>
    </div>

    <div
      v-if="!isCollapsed"
      class="position-relative"
      :class="item.posts.length ? '' : 'padding-y-sm '"
    >
      <draggable
        v-model="item.posts"
        class="playlist-draggable playlist-draggable--nested tgbDropArea tgbDropArea--graphics"
        :class="[{'empty': !item.posts.length},'zoom-' + zoom]"
        :group="{ name: 'timeline' }"
        animation="200"
        :disabled="isDraggingDisabled"
        handle=".drag-handle"
        revert-on-spill="true"
        data-test="producer-timeline-graphicGroup-droparea"
        @change="onChangeGroup"
      >
        <graphic
          v-for="graphic in graphics"
          class="playlist-layer--nested"
          :key="graphic.timeline_id"
          :root-item="item"
          :item="graphic"
          :grouped="true"
          @queued-post="onQueuePost(graphic)"
        />

        <div class="tgbDropAreaHelp" v-if="!item.posts.length">
          <p>Drag and drop graphics here</p>
        </div>
      </draggable>
    </div>
  </div>
</template>

<script>
import draggable from 'vuedraggable';

import ConduitEventsService from '../../../services/conduitEventsService';
import { getSocialPosts } from '../../../utils/production';

import TimelineMixins from '../../../mixins/timeline-mixins';
import DurationComponent from '../duration-input/DurationInput.vue';
import PreviewLiveSwitch from '../../PreviewLiveSwitch.vue';

import GraphicComponent from './TimelineGraphic.vue';

const dispatchEvent = (event) => {
  const evt = document.createEvent('Event');
  evt.initEvent(event, true, true);
  document.dispatchEvent(evt);
};

export default {
  name: 'TimelineGraphicsComponent',
  mixins: [TimelineMixins],

  data() {
    return {
      isEditingName: false,
      isCollapsed: false,
    };
  },

  components: {
    draggable,
    duration: DurationComponent,
    'preview-live-switch': PreviewLiveSwitch,
    graphic: GraphicComponent,
  },

  computed: {
    /**
    * Groups only have a duration if there's NO social graphics inside of them.
    * If there's a social graphic with individual posts, then time is determined by those.
    * @returns {Boolean}
    */
    showDuration() {
      const socialPosts = getSocialPosts(this.item);

      // Show duration if there's no social posts inside the group
      if (!socialPosts?.length) {
        return true;
      }

      // Also show duration if the only socialPosts are tagboards, not posts
      if (socialPosts.some((post) => post.type === 'tagboard')) {
        return true;
      }

      // Otherwise no duration (i.e. has social posts)
      return false;
    },

    /**
     * Disable nested draggable if user is dragging a non-graphic
     */
    isDraggingDisabled() {
      const { type } = this.$store.state.drag;
      return type && type !== 'graphic';
    },

    canMergeUp() {
      // Can merge graphics group into another graphic or group
      return ['graphic', 'graphics'].includes(this.prev?.layout);
    },

    canMergeDown() {
      // Can merge graphics group into another graphic or group
      return ['graphic', 'graphics'].includes(this.next?.layout);
    },

    graphics() {
      return this.item.posts;
    },

    graphicsCount() {
      return `${this.graphics.length} Graphic${this.graphics.length === 1 ? '' : 's'}`;
    },

    title() {
      return this.item.title;
    },
  },
  methods: {
    /**
    * Called whenever a social post is queued in an inner graphic.
    * Used to clear the queued post on other graphics in the group.
    */
    onQueuePost(graphic) {
      this.item.posts.forEach((post) => {
        if (post.timeline_id === graphic.timeline_id) {
          // Ignore the graphic with the new queued post
          return;
        }

        const [socialId] = post.socialData || [];
        if (socialId && post.fields[socialId]) {
          // If post has a social element, then remove the queued post
          // eslint-disable-next-line no-param-reassign
          post.fields[socialId].queuedPost = null;
        }
      });
    },

    onChangeGroup(e) {
      if (e.added) {
        const { element, newIndex } = e.added;
        const { group } = this.$store.state.drag;

        // Clear queuedPost on social graphics when added to the group
        const [id] = element.socialData || [];
        if (id) {
          const fields = element.fields[id];
          if (fields) {
            fields.queuedPost = null;
          }
        }

        // This should be handled with global state,
        // but we'll just let the tabs know to clear for now
        dispatchEvent('clearTabSelection');

        if (group && group.length) {
          // Add entire group to playlist
          this.$store.dispatch('replaceGroupItem', {
            groupIndex: this.index,
            index: newIndex,
            items: group,
          });
        }

        ConduitEventsService.track(
            'producer.dragged.graphics-to-group',
            {
              production_id: this.production._id,
              count: group?.length || 1,
              graphics: (group?.length) ? group : [element],
              graphicsGroup: this.item,
            },
            {
              ownerId: this.production.owner,
              userId: this.$store.state.user.id,
              isStaff: this.$store.getters.isStaff,
            },
        );
      }

      // Always send updateTimeline event so other connected clients see change
      this.$store.dispatch('generateShortUrls');
      this.$store.dispatch('trackSportradarInGameElement');
      this.$store.dispatch('updateTimeline', { force: true });

      if (this.isCurrentSampleLayout) {
        // Re-render preview
        this.$store.dispatch('updatePreviewTimeline', { force: true });
      }
    },

    toggleCollapse() {
      this.isCollapsed = !this.isCollapsed;
    },

    editName() {
      this.name = this.item.title;
      this.isEditingName = true;

      this.$nextTick(() => {
        // Autofocus and highlight all text
        this.$refs.nameInput.focus();
        this.$refs.nameInput.select();
      });
    },

    cancelEditingName() {
      this.isEditingName = false;
    },

    saveName() {
      if (!this.isEditingName) { return; }

      this.isEditingName = false;

      // User left the name blank, so revert back to original name
      if (!this.name || !this.name.trim()) { return; }

      // Sucks updating this directly here,
      // we should probably issue a proper state change through Vuex
      this.item.title = this.name.trim();

      // Call updateTimeline to let other connected clients see the name change
      this.$store.dispatch('updateTimeline');

      ConduitEventsService.track(
          'producer.renamed.group',
          {
            production_id: this.production._id,
            name: this.item.title,
          },
          {
            ownerId: this.production.owner,
            userId: this.$store.state.user.id,
            isStaff: this.$store.getters.isStaff,
          },
      );
    },

    mergeUp() {
      this.$store.dispatch('mergeGraphics', {
        i1: this.index - 1,
        i2: this.index,
      });
    },

    mergeDown() {
      this.$store.dispatch('mergeGraphics', {
        i1: this.index,
        i2: this.index + 1,
      });
    },
  },
};
</script>
